import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, IconButton, Pagination, Stack, Button,
  Select, MenuItem, FormControl, InputLabel,
} from '@mui/material';
import { Visibility as ViewIcon } from '@mui/icons-material';
import api from '../api/axios';
import { color } from '../theme/tokens';
import {
  PageHeader, Toolbar, DataTable, StatusBadge, FormDialog, useFeedback,
} from './ui';

// Fields that should span the full dialog width (long free text).
const FULL_WIDTH_FIELDS = ['MESSAGE', 'ADDRESS'];

const DetailDialog = ({ item, fields, open, onClose }) => (
  <FormDialog
    open={open}
    onClose={onClose}
    title={item?.NAME || 'Details'}
    maxWidth="sm"
    actions={<Button onClick={onClose} variant="outlined" color="inherit">Close</Button>}
  >
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
        gap: 2,
        mt: 1,
      }}
    >
      {fields.map(({ label, key, render }) => (
        <Box
          key={key}
          sx={FULL_WIDTH_FIELDS.includes(key) ? { gridColumn: { xs: 'auto', sm: '1 / -1' } } : undefined}
        >
          <Typography sx={{ fontSize: '0.75rem', color: color.ink[500], display: 'block' }}>
            {label}
          </Typography>
          <Typography sx={{ fontSize: '0.875rem', fontWeight: 500, color: color.ink[900], wordBreak: 'break-word' }}>
            {render ? render(item?.[key]) : (item?.[key] || '—')}
          </Typography>
        </Box>
      ))}
    </Box>
  </FormDialog>
);

const InboxTable = ({ title, subtitle, apiPath, columns, detailFields, searchPlaceholder }) => {
  const { error: notifyError } = useFeedback();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [inputVal, setInputVal] = useState('');
  const [sort, setSort] = useState('newest');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [selected, setSelected] = useState(null);

  const fetchItems = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(apiPath, { params: { page, pagesize: 20, search: search || undefined, sort } });
      setItems(res.data.data.items);
      setTotalPages(res.data.data.pagination.totalPages);
      setTotal(res.data.data.pagination.total);
    } catch {
      notifyError('Failed to load data');
    } finally {
      setLoading(false);
    }
  }, [apiPath, page, search, sort, notifyError]);

  useEffect(() => { fetchItems(); }, [fetchItems]);

  // Debounce search
  useEffect(() => {
    const t = setTimeout(() => { setSearch(inputVal); setPage(1); }, 400);
    return () => clearTimeout(t);
  }, [inputVal]);

  const tableColumns = [
    ...columns.map(c => ({ key: c.key, label: c.label, render: c.render })),
    {
      key: '__view',
      label: 'View',
      align: 'center',
      width: 72,
      render: (_v, row) => (
        <IconButton
          size="small"
          onClick={(e) => { e.stopPropagation(); setSelected(row); }}
          aria-label="View details"
        >
          <ViewIcon fontSize="small" />
        </IconButton>
      ),
    },
  ];

  return (
    <Box>
      <PageHeader
        title={title}
        subtitle={subtitle}
        actions={<StatusBadge label={`${total} total`} tone="neutral" dot={false} />}
      />

      <Box sx={{ mb: 2 }}>
        <Toolbar
          search={inputVal}
          onSearch={setInputVal}
          searchPlaceholder={searchPlaceholder}
          onRefresh={fetchItems}
          refreshing={loading}
        >
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel id="inbox-sort-label">Sort By</InputLabel>
            <Select
              labelId="inbox-sort-label"
              label="Sort By"
              value={sort}
              onChange={(e) => { setSort(e.target.value); setPage(1); }}
            >
              <MenuItem value="newest">Newest first</MenuItem>
              <MenuItem value="oldest">Oldest first</MenuItem>
            </Select>
          </FormControl>
        </Toolbar>
      </Box>

      <DataTable
        columns={tableColumns}
        rows={items}
        rowKey="SRNO"
        loading={loading}
        minWidth={760}
        emptyTitle="No records found"
      />

      {totalPages > 1 && (
        <Stack alignItems="center" sx={{ mt: 3 }}>
          <Pagination count={totalPages} page={page} onChange={(_e, v) => setPage(v)} color="primary" />
        </Stack>
      )}

      <DetailDialog
        item={selected}
        fields={detailFields}
        open={!!selected}
        onClose={() => setSelected(null)}
      />
    </Box>
  );
};

export default InboxTable;
